// @ts-nocheck
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useAuth0 } from '@auth0/auth0-react';
import { Box, Grid, IconButton, Typography } from '@mui/material';
import {
  RemoveCircleOutlined as DeleteIcon,
  AddCircleOutline as AddIcon,
} from '@mui/icons-material';

import { FHButton, FHInputField } from '../../../base';
import { clientFamilyBackgroundActions } from '../../../../store';
import { useTreatmentPlanEditable } from '../hooks/useTreatmentPlanEditable';
import { validateSync, validateSchema, getAllInvalidFieldNamesAuto } from '../../../../helpers';
import { FORTA_BLUE, FORTA_WHITE } from '../../../../utils/colors';
import { Themes } from '../../../../constants';
import { siblingSchema } from './validationSchema';

import type { ISibling } from '../../../../utils/types';
import type { ChangeEvent } from 'react';

const SiblingsTableHeader = () => (
  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
    <Box width={35}></Box>
    <Grid container spacing={2}>
      <Grid item xs={4}>
        <Typography fontWeight="bold">Relation*</Typography>
      </Grid>
      <Grid item xs={2}>
        <Typography fontWeight="bold">Age*</Typography>
      </Grid>
      <Grid item xs={6}>
        <Typography fontWeight="bold">
          Diagnosis (if not applicable, please write “none”).
        </Typography>
      </Grid>
    </Grid>
  </Box>
);

function Siblings() {
  const dispatch = useDispatch();
  const { getAccessTokenSilently } = useAuth0();
  const isEditable = useTreatmentPlanEditable();
  const { current: treatmentPlan } = useSelector((state: any) => state.treatmentPlans);
  const { clientFamilyBackground, siblings, triggerValidationsOnPageLoad } = useSelector(
    (state: any) => state.clientFamilyBackgrounds,
  );
  const [list, setList] = useState<ISibling[]>([]);
  const [errors, setErrors] = useState<{ [key: number]: { [key: string]: boolean } }>({});

  const listOfValidationKeys = ['name', 'age', 'diagnosis'];

  useEffect(() => {
    setErrors({});
    if (!list || list.length === 0) {
      return;
    }
    if (list.length > 0 && triggerValidationsOnPageLoad) {
      const allValidationErrors: Record<number, Record<string, boolean>> = {};
      list.forEach((sibling: ISibling, index: number) => {
        const validationErrors = getAllInvalidFieldNamesAuto(siblingSchema, sibling);
        allValidationErrors[index] = validationErrors;
      });
      setErrors((prev: any) => ({
        ...prev,
        ...allValidationErrors,
      }));
    }

    list.forEach((sibling: ISibling, index: number) => {
      if (sibling && Object.keys(sibling).length !== 0) {
        listOfValidationKeys.forEach((key: string) => {
          handleValidate(index, key, sibling);
        });
      }
    });
  }, [list]);

  useEffect(() => {
    if (!siblings || !treatmentPlan) return;

    setList(siblings || []);
  }, [siblings, treatmentPlan]);

  useEffect(() => {
    if (!treatmentPlan) return;

    if (Object.keys(errors).length === 0) {
      dispatch(
        clientFamilyBackgroundActions.updateState({
          key: 'siblingErrorCounts',
          data: 0,
        }),
      );
      return;
    }

    let errorCount = 0;
    Object.values(errors).forEach((fieldErrors) => {
      Object.values(fieldErrors).forEach((hasError) => {
        if (hasError) errorCount++;
      });
    });
    dispatch(
      clientFamilyBackgroundActions.updateState({
        key: 'siblingErrorCounts',
        data: errorCount,
      }),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [errors]);

  const handleAddNew = async () => {
    if (!clientFamilyBackground?.id) {
      const response = await dispatch(
        clientFamilyBackgroundActions.createClientFamilyBackground({
          treatmentPlanId: treatmentPlan?.id,
          data: {
            other_individuals_in_home: '',
          },
          getAccessTokenSilently,
        }),
      );
      if (response.error) {
        return;
      }
    }
    dispatch(
      clientFamilyBackgroundActions.updateState({
        key: 'siblings',
        data: [...siblings, {}],
      }),
    );
  };

  const handleDelete = async (index: number) => {
    if (!treatmentPlan || !clientFamilyBackground) return;

    if (Object.hasOwn(list[index], 'id')) {
      const res = await dispatch(
        clientFamilyBackgroundActions.removeSibling({
          treatmentPlanId: treatmentPlan.id,
          backgroundId: clientFamilyBackground.id,
          siblingId: list[index].id,
          getAccessTokenSilently,
        }),
      );
      if (res.error) return;
    }

    const filtered = list?.filter((_: ISibling, i: number) => index !== i);
    dispatch(
      clientFamilyBackgroundActions.updateState({
        key: 'siblings',
        data: filtered,
      }),
    );
  };

  const handleUpdateField = (index: number, key: string, value: any) => {
    dispatch(
      clientFamilyBackgroundActions.updateState({
        key: 'siblings',
        data: siblings?.map((pc: ISibling, i: number) =>
          index === i ? { ...pc, [key]: value } : pc,
        ),
      }),
    );
  };

  const handleSave = async (index: number, data: ISibling) => {
    if (!treatmentPlan || !clientFamilyBackground || !data) return;

    const isValid = validateSync(siblingSchema, data);
    let payload: ISibling = data;
    if (isValid) {
      // Update sibling background
      if (data.id) {
        const res = await dispatch(
          clientFamilyBackgroundActions.updateSibling({
            treatmentPlanId: treatmentPlan.id,
            backgroundId: clientFamilyBackground.id,
            data,
            getAccessTokenSilently,
          }),
        );
        if (res.error) return;

        payload = res.payload;
      } else {
        // Create new sibling background
        const res = await dispatch(
          clientFamilyBackgroundActions.createSibling({
            treatmentPlanId: treatmentPlan.id,
            backgroundId: clientFamilyBackground.id,
            data,
            index,
            getAccessTokenSilently,
          }),
        );
        if (res.error) return;

        payload = res.payload;
      }
    } else {
      dispatch(
        clientFamilyBackgroundActions.updateState({
          key: 'siblings',
          data: siblings?.map((sibling: ISibling, i: number) => (index === i ? payload : sibling)),
        }),
      );
    }
  };

  const handleValidate = (index: number, key: string, data: any) => {
    if (!data) return;

    const isValid = validateSchema(siblingSchema, key, data);
    setErrors((prev: any) => ({
      ...prev,
      [index]: prev[index] ? { ...prev[index], [key]: !isValid } : { [key]: !isValid },
    }));
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
      <Typography fontWeight="bold" textAlign={'left'} mb={1.5}>
        Sibling(s):
      </Typography>
      {list.length > 0 && <SiblingsTableHeader />}
      {list?.map((sibling: ISibling, index: number) => (
        <Box key={index} sx={{ display: 'flex', alignItems: 'center' }} mb={2}>
          <Box width={35}>
            {isEditable && (
              <IconButton sx={{ p: 0, m: 0 }} onClick={() => handleDelete(index)}>
                <DeleteIcon sx={{ color: FORTA_BLUE.primary }} />
              </IconButton>
            )}
          </Box>
          <Grid container spacing={2}>
            <Grid item xs={4}>
              <FHInputField
                fullWidth
                colorTheme={Themes.Primary}
                disabled={!isEditable}
                inputHeight={40}
                value={sibling.name}
                onChange={(e: ChangeEvent<HTMLInputElement>) =>
                  handleUpdateField(index, 'name', e.target.value)
                }
                onBlur={() => handleSave(index, sibling)}
                error={errors[index] ? errors[index]['name'] : false}
              />
            </Grid>
            <Grid item xs={2}>
              <FHInputField
                sx={{ minWidth: 'fit-content' }}
                type={'number'}
                fullWidth
                colorTheme={Themes.Primary}
                disabled={!isEditable}
                inputHeight={40}
                value={sibling.age}
                onChange={(e: ChangeEvent<HTMLInputElement>) =>
                  handleUpdateField(index, 'age', e.target.value)
                }
                onBlur={() => handleSave(index, sibling)}
                error={errors[index] ? errors[index]['age'] : false}
              />
            </Grid>
            <Grid item xs={6}>
              <FHInputField
                fullWidth
                colorTheme={Themes.Primary}
                disabled={!isEditable}
                inputHeight={40}
                value={sibling.diagnosis}
                onChange={(e: ChangeEvent<HTMLInputElement>) =>
                  handleUpdateField(index, 'diagnosis', e.target.value)
                }
                onBlur={() => handleSave(index, sibling)}
                error={errors[index] ? errors[index]['diagnosis'] : false}
              />
            </Grid>
          </Grid>
        </Box>
      ))}
      {isEditable && (
        <FHButton
          data-testid="addSiblingButton"
          colorTheme={Themes.Primary}
          startIcon={<AddIcon sx={{ color: FORTA_WHITE }} />}
          sx={{ width: 'fit-content' }}
          onClick={handleAddNew}
        >
          Add Sibling
        </FHButton>
      )}
    </Box>
  );
}

export default Siblings;
